// frontend/src/utils/friendCodeOffer.js
//
// THE OFFER VIEW-MODEL - one pure function every selling surface switches on.
//
// `GET /payments/offer` answers ONE discount kind per user (the server's
// `resolve_discount` decides: a referred user is priced by the referral
// alone). Before the friend-code program each surface read `offer.eligible`
// and `offer.discount_kind` on its own; with a third kind that has no deadline
// and spans two charges, four surfaces would each have had to learn the same
// rules. They read this object instead:
//
//   kind: "referral" | "early_upgrade" | "winback" | "none"
//
// and a referral variant can never paint an early-upgrade or win-back
// sentence, a deadline, or a countdown, because those fields are simply null
// on it.
//
// FAIL-CLOSED, as every selling surface already is: no offer, an ineligible
// offer, an unknown kind, a non-positive percent - all `none`, which renders
// exactly what each surface rendered before any offer existed.
//
// NUMBERS COME FROM THE API ONLY. Nothing here computes a price or a percent;
// it validates and carries what the server sent.

import { formatDiscountPercent, upgradeOfferCopy } from "../components/upgradeOfferText";
import { fillTemplate } from "../content/friendCode/copy";

export const OFFER_KIND = Object.freeze({
  REFERRAL: "referral",
  EARLY_UPGRADE: "early_upgrade",
  WINBACK: "winback",
  NONE: "none",
});

const KNOWN_KINDS = new Set([
  OFFER_KIND.REFERRAL,
  OFFER_KIND.EARLY_UPGRADE,
  OFFER_KIND.WINBACK,
]);

const OFFER_PLAN_KEYS = ["solo", "professional"];

const finiteOrNull = (value) => {
  if (value === null || value === undefined || value === "") return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
};

/**
 * A money value as the customer reads it: whole shekels with no decimals
 * ("159"), or two decimals when the API sent a fraction ("159.50"). Never
 * rounds a fraction away - the displayed number must be the charged number.
 *
 * @param {*} value
 * @returns {string} "" when the value is not a non-negative number
 */
export const formatShekels = (value) => {
  const n = finiteOrNull(value);
  if (n === null || n < 0) return "";
  return Number.isInteger(n) ? String(n) : n.toFixed(2);
};

/**
 * One plan's discounted pair from the offer, or null when the offer does not
 * discount that plan (missing, non-finite, free, or not actually cheaper).
 *
 * @param {object|null} offer
 * @param {"solo"|"professional"} planKey
 * @returns {{full: number, final: number}|null}
 */
export const discountedPlanPrice = (offer, planKey) => {
  const plan = offer?.plans?.[planKey];
  const full = finiteOrNull(plan?.full_price);
  const final = finiteOrNull(plan?.final_price);
  if (full === null || final === null || final <= 0 || final >= full) return null;
  return { full, final };
};

/**
 * Is this user a REFERRED-DISCOUNT customer - a partner referral whose
 * snapshot customer discount is above 0%?
 *
 * A COMMISSION-ONLY referral (the partner's customer discount is 0%) is still
 * attributed - `/users/me` carries `partner_referral` with
 * `discount_percent: 0`, and it still blocks entering a second code (that gate,
 * `canEnterCheckoutFriendCode`, reads the bare `partner_referral` on purpose) -
 * but for PRICING it is an ordinary customer: the server's `resolve_discount`
 * gives it the regular offers (early-upgrade / win-back), so no surface may
 * treat it as referred. Fail-closed: a missing or non-numeric percent is not
 * a referral discount.
 *
 * @param {object|null|undefined} user
 * @returns {boolean}
 */
export const hasReferralDiscount = (user) => {
  const percent = finiteOrNull(user?.partner_referral?.discount_percent);
  return percent !== null && percent > 0;
};

const NONE_VIEW = Object.freeze({
  kind: OFFER_KIND.NONE,
  percent: null,
  percentText: "",
  discountedChargesLeft: null,
  plans: Object.freeze({ solo: null, professional: null }),
  offerEndsAt: null,
  daysLeft: null,
  hasHotOpportunity: false,
  professionalSavingNis: null,
});

/**
 * The display model of the user's current offer.
 *
 * A user whose payload carries a `partner_referral` with a positive discount
 * (`hasReferralDiscount`) is a REFERRED user: for them only a `referral` offer
 * may ever render. A commission-only (0%) referral is not - it gets the
 * server's regular offer like any other customer. Should the offer endpoint answer any
 * other kind for such a user (it never should - `resolve_discount` returns the
 * referral or nothing), the view is `none` rather than an offer the owner ruled
 * out for referred customers. The converse is allowed: an offer of kind
 * `referral` while the user payload has not caught up yet (a code applied a
 * moment ago) IS shown - the offer is the server's fresher word.
 *
 * @param {object|null|undefined} offer  `GET /payments/offer` payload
 * @param {object|null|undefined} user   the `/users/me` payload
 * @returns {typeof NONE_VIEW}
 */
export const resolveOfferView = (offer, user) => {
  if (!offer || offer.eligible !== true) return NONE_VIEW;
  const kind = offer.discount_kind;
  if (!KNOWN_KINDS.has(kind)) return NONE_VIEW;

  const referred = hasReferralDiscount(user);
  if (referred && kind !== OFFER_KIND.REFERRAL) return NONE_VIEW;

  const percent = finiteOrNull(offer.discount_percent);
  if (percent === null || percent <= 0) return NONE_VIEW;

  const plans = {};
  OFFER_PLAN_KEYS.forEach((key) => {
    plans[key] = discountedPlanPrice(offer, key);
  });

  const isReferral = kind === OFFER_KIND.REFERRAL;
  const chargesLeft = finiteOrNull(offer.discounted_charges_left);
  const saving = finiteOrNull(offer?.plans?.professional?.discount_nis);

  return {
    kind,
    percent,
    percentText: formatDiscountPercent(percent),
    // Referral only. Null when the server did not carry it.
    discountedChargesLeft: isReferral && chargesLeft !== null ? chargesLeft : null,
    plans,
    // Deadline + countdown are early-upgrade / win-back facts. A referral has
    // neither, so they are forced null even if a payload carried them.
    offerEndsAt: isReferral ? null : offer.offer_ends_at || null,
    daysLeft: isReferral ? null : finiteOrNull(offer.days_left),
    hasHotOpportunity: offer.has_hot_opportunity === true,
    professionalSavingNis: saving !== null && saving > 0 ? Math.round(saving) : null,
  };
};

/**
 * The offer-kind property for the `upgrade_offer_*` analytics events: a fixed
 * token, never the code. Only a REFERRAL view carries it - the early-upgrade /
 * win-back events keep exactly the shape they have always been recorded in, so
 * historical rows stay comparable, and "no `discount_kind`" keeps meaning the
 * pre-friend-code population.
 *
 * @param {object|null|undefined} view  from `resolveOfferView`
 * @returns {{discount_kind?: "referral"}}
 */
export const offerKindEventParams = (view) =>
  view && view.kind === OFFER_KIND.REFERRAL ? { discount_kind: OFFER_KIND.REFERRAL } : {};

/**
 * Which approved price lines a referral checkout block shows, for one plan.
 *
 *   2+ discounted charges left -> "first + second month" + "from the third"
 *   exactly 1 left             -> "this charge" + "from the next charge"
 *                                 (approved 2026-10-04 - see copy.js)
 *   unknown                    -> none (the plan cards' struck prices stand)
 *
 * @param {number|null} discountedChargesLeft
 * @returns {string[]} copy keys, in order
 */
export const referralPriceLineKeys = (discountedChargesLeft) => {
  if (discountedChargesLeft === null || discountedChargesLeft === undefined) return [];
  if (discountedChargesLeft >= 2) return ["offer.priceFirstTwoMonths", "offer.priceFromThirdMonth"];
  if (discountedChargesLeft === 1) return ["offer.priceThisCharge", "offer.priceFromNextCharge"];
  return [];
};

/**
 * Does the user's payload say a referral discount is still ahead of them?
 * The gate `useUpgradeOffer` widens on (trialing, active AND lapsed).
 *
 * Mirrors the backend's `ReferralPricingState.is_discounted`: BOTH a
 * discounted charge still ahead AND a positive snapshot percent. A
 * commission-only referral (the partner's customer discount is 0%) is not a
 * referred-discount customer - their price is simply the list price, so they
 * take the regular checkout path (no plan buttons held behind the referral
 * price read and its retries).
 *
 * @param {object|null|undefined} user
 * @returns {boolean}
 */
export const hasReferralDiscountAhead = (user) => {
  const left = finiteOrNull(user?.partner_referral?.discounted_charges_left);
  return left !== null && left > 0 && hasReferralDiscount(user);
};

/**
 * The navbar chip's label + whether it carries an offer, from the view.
 *
 *   referral      -> "P% הנחת חבר" (approved), an offer chip
 *   early/winback -> CD-01 "שדרג - חיסכון עד X ש"ח" when there is a positive
 *                    Professional saving, else the plain label; an offer chip
 *   none          -> the plain label, no offer
 *
 * @param {object} view       from `resolveOfferView`
 * @param {{isHebrew: boolean, plainLabel: string}} options
 * @returns {{label: string, hasOffer: boolean}}
 */
export const navbarChipView = (view, { isHebrew, plainLabel }) => {
  if (!view || view.kind === OFFER_KIND.NONE) return { label: plainLabel, hasOffer: false };
  if (view.kind === OFFER_KIND.REFERRAL) {
    return {
      label: fillTemplate("offer.navbarChip", isHebrew, { percent: view.percentText }),
      hasOffer: true,
    };
  }
  return {
    label: view.professionalSavingNis
      ? upgradeOfferCopy.navbarPillWithSaving({
          isHebrew,
          savingNis: view.professionalSavingNis,
          discountPercent: view.percent,
        })
      : plainLabel,
    hasOffer: true,
  };
};
